import {Logger} from '../utils/managers/logger';
import {openDB, IDBPDatabase, IDBPCursorWithValue, StoreKey} from 'idb';
import ms, {StringValue} from 'ms';
import moment from 'moment';

export interface MainDatabaseSyncLog {
  id: string;
  date: Date;
}

export enum MainDatabaseStore {
  SyncLogs = 'sync-logs',
  ActivitiesSectors = 'activities-sectors',
  ActivitiesGroups = 'activities-groups',
  ActivityTypes = 'activity-types',
  // ---- Caching Care Plan Configurations doesn't work well with the current implementation. DIsabling it for now. -----
  //            --- A proper cache invalidation strategy should be implemented to avoid data inconsistency. ---
  // CarePlanConfigurations = 'care-plan-configurations',
}

export class MainDatabase {
  // Singleton Instance
  private static _instance: MainDatabase;

  // Private
  private logger: Logger;
  private _database!: IDBPDatabase<any>;

  // Constants
  public static ID = 'id';

  static get instance(): MainDatabase {
    if (!this._instance) {
      this._instance = new MainDatabase();
    }
    return this._instance;
  }

  constructor() {
    // Create Logger
    this.logger = new Logger('MainDatabase');
  }

  private async getDatabase(): Promise<IDBPDatabase<any>> {
    if (!this._database) {
      this._database = await openDB('main', 1, {
        upgrade: (db, oldVersion, newVersion) => {
          this.logger.info('Database Upgrade', {oldVersion, newVersion});
          switch (oldVersion) {
            case 0:
              if (!db.objectStoreNames.contains(MainDatabaseStore.SyncLogs)) {
                db.createObjectStore(MainDatabaseStore.SyncLogs, {keyPath: MainDatabase.ID});
              }

              if (!db.objectStoreNames.contains(MainDatabaseStore.ActivitiesSectors)) {
                db.createObjectStore(MainDatabaseStore.ActivitiesSectors, {
                  keyPath: MainDatabase.ID,
                });
              }

              if (!db.objectStoreNames.contains(MainDatabaseStore.ActivitiesGroups)) {
                db.createObjectStore(MainDatabaseStore.ActivitiesGroups, {
                  keyPath: MainDatabase.ID,
                });
              }

              if (!db.objectStoreNames.contains(MainDatabaseStore.ActivityTypes)) {
                db.createObjectStore(MainDatabaseStore.ActivityTypes, {keyPath: MainDatabase.ID});
              }

            // ---- Caching Care Plan Configurations doesn't work well with the current implementation. DIsabling it for now. -----
            // if (!db.objectStoreNames.contains(MainDatabaseStore.CarePlanConfigurations)) {
            //   db.createObjectStore(MainDatabaseStore.CarePlanConfigurations, {
            //     keyPath: MainDatabase.ID,
            //   });
            // }
          }
        },
      });
    }
    return this._database;
  }

  public async count(store: MainDatabaseStore): Promise<number> {
    const db = await this.getDatabase();
    return db.count(store);
  }

  public async getAll<T>(store: MainDatabaseStore): Promise<Array<T>> {
    const db = await this.getDatabase();
    return db.getAll(store);
  }

  public async getById<T>(store: MainDatabaseStore, id: string): Promise<T | undefined> {
    const db = await this.getDatabase();
    return db.get(store, id);
  }

  public async put<T>(store: MainDatabaseStore, data: T): Promise<StoreKey<any, any>> {
    const db = await this.getDatabase();
    return db.put(store, data);
  }

  public async openCursor(
    store: MainDatabaseStore
  ): Promise<IDBPCursorWithValue<any, any, any, unknown, any> | null> {
    const db = await this.getDatabase();
    const tx = db.transaction(store, 'readonly');
    return tx.store.openCursor();
  }

  public isDataExpired(date: Date, expiration: StringValue = '30d', exactDate?: Date): boolean {
    // If an exact date is provided, check against it
    if (exactDate && date < exactDate) {
      return true;
    }

    // By default, all data expires after 30 days
    const expirationDate = moment(date).add(ms(expiration), 'ms').toDate();
    return expirationDate < new Date();
  }

  public async clearAllData(): Promise<void> {
    const db = await this.getDatabase();
    // Get all stores from enum MainDatabaseStore
    for (const store of Object.values(MainDatabaseStore)) {
      await db.clear(store);
    }
  }
}
